import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import Spinner from "@/components/atoms/Spinner";
import ComingSoon from "@/components/ComingSoon";
import MarketingLayout from "@/layouts/MarketingLayout";
import PortalLayout from "@/layouts/PortalLayout";
import SovereignLayout from "@/layouts/SovereignLayout";

// Eager: Home + critical paths
import HomePage from "@/pages/HomePage";
import LoginPage from "@/pages/LoginPage";
import SignupPage from "@/pages/SignupPage";

// Lazy: everything else
const HostingPage = lazy(() => import("@/pages/products/HostingPage"));
const WeaverPage = lazy(() => import("@/pages/products/WeaverPage"));
const TerraLynXPage = lazy(() => import("@/pages/products/TerraLynXPage"));
const ExaMonsterPage = lazy(() => import("@/pages/products/ExaMonsterPage"));
const EmailsMonsterPage = lazy(() => import("@/pages/products/EmailsMonsterPage"));
const ZbraLynXPage = lazy(() => import("@/pages/products/ZbraLynXPage"));
const OmniExodusPage = lazy(() => import("@/pages/products/OmniExodusPage"));
const SignalPage = lazy(() => import("@/pages/products/SignalPage"));
const ArchivePage = lazy(() => import("@/pages/products/ArchivePage"));
const LedgerPage = lazy(() => import("@/pages/products/LedgerPage"));
const LedgerPulseV2 = lazy(() => import("@/pages/LedgerPulseV2"));
const VerifyPage = lazy(() => import("@/pages/VerifyPage"));
const PricingPage = lazy(() => import("@/pages/PricingPage"));
const ResetPasswordPage = lazy(() => import("@/pages/ResetPasswordPage"));
const PortalDashboard = lazy(() => import("@/pages/portal/PortalDashboard"));
const SovereignDashboard = lazy(() => import("@/pages/sovereign/SovereignDashboard"));

// Portal pages
const LazyPortalPages = lazy(() => import("@/pages/portal/PortalPages").then(m => ({ default: m.PortalServices })));
const LazyPortalBilling = lazy(() => import("@/pages/portal/PortalPages").then(m => ({ default: m.PortalBilling })));
const ExodusWizard = lazy(() => import("@/pages/portal/ExodusWizard"));

// Institutional content pages (Day 11 R1 unblock — replaces ComingSoon for F500-audit-visible routes)
const TrustPage = lazy(() => import("@/pages/TrustPage"));
const AboutPage = lazy(() => import("@/pages/AboutPage"));
const PrivacyPage = lazy(() => import("@/pages/PrivacyPage"));
const TermsPage = lazy(() => import("@/pages/TermsPage"));

const Loading = () => (
  <div className="min-h-screen bg-obsidian flex items-center justify-center"><Spinner size="lg" /></div>
);

const MarketingComing = ({ title, desc }) => (
  <MarketingLayout><ComingSoon title={title} description={desc} /></MarketingLayout>
);

const PortalComing = ({ title, desc }) => (
  <PortalLayout><ComingSoon title={title} description={desc} backTo="/portal" backLabel="Dashboard" /></PortalLayout>
);

const SovereignComing = ({ title, desc }) => (
  <SovereignLayout><ComingSoon title={title} description={desc} backTo="/sovereign" backLabel="Commander" /></SovereignLayout>
);

const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, loading } = useAuth();
  if (loading) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  if (adminOnly && user.role !== "admin") return <Navigate to="/portal" replace />;
  return children;
};

function AppRoutes() {
  return (
    <Suspense fallback={<Loading />}>
      <Routes>
        {/* ── Public Marketing ── */}
        <Route path="/" element={<HomePage />} />
        <Route path="/products" element={<MarketingComing title="All Products" desc="Complete product matrix coming soon." />} />
        <Route path="/products/weaver" element={<WeaverPage />} />
        <Route path="/products/hosting" element={<HostingPage />} />
        <Route path="/products/hosting/shared" element={<MarketingComing title="Shared Hosting" desc="Shared hosting plans — launching soon." />} />
        <Route path="/products/hosting/vps" element={<MarketingComing title="VPS Hosting" desc="Virtual private server plans — launching soon." />} />
        <Route path="/products/hosting/reseller" element={<MarketingComing title="Reseller Hosting" desc="Reseller hosting plans — launching soon." />} />
        <Route path="/products/hosting/dedicated" element={<MarketingComing title="Dedicated Servers" desc="Bare metal dedicated servers — launching soon." />} />
        {/* 2026-04-27 rebrand: Microhost → DrkLynX Hosting. Preserve old URLs to maintain inbound links + SEO equity. */}
        <Route path="/products/microhost" element={<Navigate to="/products/hosting" replace />} />
        <Route path="/products/microhost/shared" element={<Navigate to="/products/hosting/shared" replace />} />
        <Route path="/products/microhost/vps" element={<Navigate to="/products/hosting/vps" replace />} />
        <Route path="/products/microhost/reseller" element={<Navigate to="/products/hosting/reseller" replace />} />
        <Route path="/products/microhost/dedicated" element={<Navigate to="/products/hosting/dedicated" replace />} />

        <Route path="/why" element={<MarketingComing title="Why DrkLynX" desc="The sovereign stack for the post-quantum era. Full pillar page coming soon." />} />
        <Route path="/why/post-quantum" element={<MarketingComing title="Post-Quantum Ready Architecture" desc="Deep-dive into our Phase A high-velocity BFT HMAC-SHA256 attestation chain in production today, plus the Phase B drop-in primitive swap to NIST FIPS 204 ML-DSA-65 in flight, with ML-KEM-768 hybrid key exchange." />} />
        <Route path="/why/sovereignty" element={<MarketingComing title="Sovereign Data Doctrine" desc="Your data, your keys, your math." />} />
        <Route path="/why/vs/aws" element={<MarketingComing title="DrkLynX vs AWS" desc="Why post-quantum sovereignty beats vendor lock-in." />} />
        <Route path="/why/vs/digitalocean" element={<MarketingComing title="DrkLynX vs DigitalOcean" desc="Sovereign infrastructure for the rest of us." />} />
        <Route path="/why/vs/hostinger" element={<MarketingComing title="DrkLynX vs Hostinger" desc="PQC-hardened hosting vs. commodity hosting." />} />
        <Route path="/why/vs/cloudflare" element={<MarketingComing title="DrkLynX vs Cloudflare" desc="Edge delivery with cryptographic sovereignty." />} />

        <Route path="/ledger-pulse" element={<LedgerPulseV2 />} />
        <Route path="/ledger-pulse/verify/:hash" element={<VerifyPage />} />
        {/* Pivot 2026-05-07: short /verify aliases referenced from TrustPage and PrivacyPage */}
        <Route path="/verify" element={<VerifyPage />} />
        <Route path="/verify/:hash" element={<VerifyPage />} />
        {/* Pivot 2026-05-13: /domains marketing surface — live registrar storefront is at drklynx.com/domains/* (drklynx-domains Worker route binding). Customer hits /domains for marketing, then continues into the Worker-served storefront under the same hostname. */}
        <Route path="/domains" element={<MarketingComing title="DrkLynX Domains" desc="Sovereign domain registration with PQC-attested ownership receipts. Live registrar storefront — search, quote, and checkout — runs at drklynx.com/domains/* on the drklynx-domains Worker (porkbun_configured · stripe_configured · attestation_configured all true on /domains/api/health). Phase A attested today (Ed25519 + HMAC-SHA256, JWKS rotation at attest.gammaqc.com); Phase B target ML-DSA-65 / NIST FIPS-204 drop-in primitive swap." />} />
        {/* Pivot 2026-05-07: /products/sovereignty-report link from AboutPage. */}
        <Route path="/products/sovereignty-report" element={<MarketingComing title="Sovereignty Report" desc="Post-Quantum Ready audit document — Phase A Ed25519 + HMAC-SHA256 attestation chain, third-party verifiable offline at attest.gammaqc.com. Schema invariant through the Phase B primitive swap to ML-DSA-65 / NIST FIPS-204. Cadence and form pending; design partners welcome." />} />

        {/* 2026-05-04 unified rebrand: DrkLynX module names + old-URL 301 redirects for SEO equity */}
        <Route path="/products/weaver" element={<TerraLynXPage />} />
        <Route path="/products/zbralynx" element={<ZbraLynXPage />} />
        <Route path="/products/crossing" element={<OmniExodusPage />} />
        <Route path="/products/signal" element={<SignalPage />} />
        <Route path="/products/archive" element={<ArchivePage />} />
        <Route path="/products/ledger" element={<LedgerPage />} />
        <Route path="/products/exa-monster" element={<Navigate to="/products/archive" replace />} />
        <Route path="/products/emails-monster" element={<Navigate to="/products/signal" replace />} />
        {/* Legacy 301s to new module paths */}
        <Route path="/products/terralynx" element={<Navigate to="/products/weaver" replace />} />
        <Route path="/products/cadence" element={<Navigate to="/products/weaver" replace />} />
        <Route path="/products/relay" element={<Navigate to="/products/zbralynx" replace />} />
        <Route path="/products/omni-exodus" element={<Navigate to="/products/crossing" replace />} />
        <Route path="/pricing" element={<PricingPage />} />

        <Route path="/docs" element={<MarketingComing title="Documentation" desc="Developer docs with full-text search — publishing soon." />} />
        <Route path="/docs/getting-started" element={<MarketingComing title="Getting Started" desc="Quick-start guide for DrkLynX developers." />} />
        <Route path="/docs/api" element={<MarketingComing title="API Reference" desc="Complete API documentation." />} />

        <Route path="/resources/blog" element={<MarketingComing title="Blog" desc="Quantum threat intel and sovereign infrastructure insights." />} />
        <Route path="/resources/blog/:slug" element={<MarketingComing title="Blog Post" desc="Article content coming soon." />} />
        <Route path="/resources/case-studies" element={<MarketingComing title="Case Studies" desc="Customer success stories — collecting now." />} />
        <Route path="/resources/compare" element={<MarketingComing title="Compare DrkLynX" desc="Head-to-head comparisons with legacy infrastructure." />} />
        <Route path="/resources/whitepapers" element={<MarketingComing title="Whitepapers" desc="Technical whitepapers on PQC implementation." />} />

        <Route path="/status" element={<MarketingComing title="System Status" desc="Real-time uptime and mesh health monitoring." />} />
        <Route path="/trust" element={<TrustPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/contact" element={<MarketingComing title="Contact" desc="Async-only contact. No phone, no live chat. Automation carries every flow." />} />

        <Route path="/legal/terms" element={<TermsPage />} />
        <Route path="/legal/privacy" element={<PrivacyPage />} />
        <Route path="/legal/acceptable-use" element={<MarketingComing title="Acceptable Use Policy" desc="What you can and cannot do on the DrkLynX platform." />} />
        <Route path="/legal/dpa" element={<MarketingComing title="Data Processing Agreement" desc="GDPR/CCPA-compliant data processing terms." />} />

        {/* ── Auth ── */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/forgot-password" element={<ResetPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />

        {/* ── Portal (authenticated) ── */}
        <Route path="/portal" element={<ProtectedRoute><PortalDashboard /></ProtectedRoute>} />
        <Route path="/portal/services" element={<ProtectedRoute><LazyPortalPages /></ProtectedRoute>} />
        <Route path="/portal/services/:id" element={<ProtectedRoute><PortalComing title="Service Detail" desc="Live metrics, logs, credentials, and migration controls." /></ProtectedRoute>} />
        <Route path="/portal/domains" element={<ProtectedRoute><PortalComing title="Domains" desc="Domain registration, DNS management, and PQC SSL." /></ProtectedRoute>} />
        <Route path="/portal/billing" element={<ProtectedRoute><LazyPortalBilling /></ProtectedRoute>} />
        <Route path="/portal/tickets" element={<ProtectedRoute><PortalComing title="Support Tickets" desc="Async support ticketing. Agent-answered, escalation-ready." /></ProtectedRoute>} />
        <Route path="/portal/migrate" element={<ProtectedRoute><ExodusWizard /></ProtectedRoute>} />
        <Route path="/portal/keys" element={<ProtectedRoute><PortalComing title="API Keys" desc="API keys, PQC attestation keys, and webhook secrets." /></ProtectedRoute>} />
        <Route path="/portal/teams" element={<ProtectedRoute><PortalComing title="Teams" desc="Seat management and role-based access." /></ProtectedRoute>} />
        <Route path="/portal/settings" element={<ProtectedRoute><PortalComing title="Settings" desc="Profile, security, notifications, and data export." /></ProtectedRoute>} />
        <Route path="/portal/usage" element={<ProtectedRoute><PortalComing title="Usage" desc="Consumption analytics across all services." /></ProtectedRoute>} />

        {/* ── Sovereign Console (admin only) ── */}
        <Route path="/sovereign" element={<ProtectedRoute adminOnly><SovereignDashboard /></ProtectedRoute>} />
        <Route path="/sovereign/mesh" element={<ProtectedRoute adminOnly><SovereignComing title="Edge Mesh" desc="Sovereign Edge Mesh · ~1,400 Cloudflare zones · Phase A attestation health grid." /></ProtectedRoute>} />
        <Route path="/sovereign/agents" element={<ProtectedRoute adminOnly><SovereignComing title="Agents" desc="NRP distribution and Cortex layer activity." /></ProtectedRoute>} />
        <Route path="/sovereign/revenue" element={<ProtectedRoute adminOnly><SovereignComing title="Revenue Intelligence" desc="Full P&L surface across all products." /></ProtectedRoute>} />
        <Route path="/sovereign/cortex" element={<ProtectedRoute adminOnly><SovereignComing title="Cortex" desc="Flash / Working / Deep layer message feed." /></ProtectedRoute>} />
        <Route path="/sovereign/ert" element={<ProtectedRoute adminOnly><SovereignComing title="ERT" desc="21-seat evaluation feed and Sacred Pipeline." /></ProtectedRoute>} />
        <Route path="/sovereign/alerts" element={<ProtectedRoute adminOnly><SovereignComing title="Alerts" desc="Entropy signals and threat feed." /></ProtectedRoute>} />
        <Route path="/sovereign/audit" element={<ProtectedRoute adminOnly><SovereignComing title="Audit Trail" desc="Post-Quantum Ready audit trail · Phase A live, Phase B (ML-DSA-65) primitive swap in flight." /></ProtectedRoute>} />

        {/* Legacy routes redirect */}
        <Route path="/admin" element={<Navigate to="/sovereign" replace />} />
        <Route path="/dashboard" element={<Navigate to="/portal" replace />} />
        <Route path="/blog" element={<Navigate to="/resources/blog" replace />} />
        <Route path="/blog/:slug" element={<Navigate to="/resources/blog" replace />} />

        {/* 404 */}
        <Route path="*" element={<MarketingComing title="404 — Page Not Found" desc="This route doesn't exist in the DrkLynX empire." />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
